// ---------------------------------------------------------------------------
// Home. Four routes out: create, join by code, browse, solo.
//
// Five things here are deliberate rather than incidental:
//
//   1. Joining by code looks the room up over HTTP before it navigates. A
//      mistyped code has to fail here, with a reason, rather than on a room page
//      that can say nothing useful about a room that was never there.
//   2. **The whole attempt resolves in place, including the socket, and that is
//      true of all four buttons.** The player pressed the button on this page,
//      so this page is where they wait: the boxes grey out, the Join button
//      turns into a spinner at exactly its own width, and a refusal restores
//      the field with the reason under it. Nothing navigates until there is a
//      room to navigate into.
//
//      Create and Play solo followed the old shape for a while longer, and it
//      showed: they navigated the moment the HTTP call answered, so the room
//      page's first frame was its cold-arrival notice — "Getting you a seat" —
//      flashing up and gone again in the time the socket took. Pressing a
//      button and being shown a wait for something that had already happened
//      reads as a stutter, not as progress. They now take the same three waits
//      behind one spinner, and the room page's first frame is the lobby.
//
//      That is three waits behind one spinner — the lookup, the bot-protection
//      challenge, and the socket join — and it is three rather than one because
//      of what used to happen instead. This screen did the lookup, navigated,
//      and the room page started again from nothing: a second lookup, a second
//      challenge and a connect, behind a page of its own that said "Getting you
//      a seat". One action, two screens, two spinners. The connection lives
//      above the router now (`hooks/roomConnection.tsx`) and survives the
//      navigation, so the room page's first frame is the lobby.
//
//      Everything the socket can refuse therefore has to be readable HERE: no
//      such room, the room is full, you are banned. They
//      arrive on `gameStore.joinError` and `gameStore.deadEnd`, land in the same
//      reserved line under the code boxes, and leave the field enabled with the
//      code still in it.
//
//      The challenge needs no surface on this screen. `getTurnstileToken` mounts
//      its own centred card at the document level and only reveals it if
//      Cloudflare actually asks — see lib/challengeModal.ts.
//   3. The name is written straight to playerStore as it is typed. It is the
//      same identity every screen reads, so there is no local copy to fall out
//      of step with it.
//   4. There is no colour picker here. Colour is assigned on arrival and changed
//      in the lobby, where the picker can see which colours are already taken
//      and the server refuses a collision outright. Picking one here decided
//      nothing: the server silently reassigned a clashing choice on join, with
//      no event and no message, so a player picked amber and arrived indigo.
//   5. Every control that can fail has a reserved line under it, sized whether
//      or not there is anything in it, so a refusal appears without pushing the
//      rest of the page down.
//
// Rooms are created private, which is `DEFAULT_SETTINGS` and what the backend
// does with no visibility given. Listing one publicly is a switch in the lobby.
//
// Signing in is not on this screen at all any more. It lives in the header, with
// the other thing that is about the player rather than about the game.
//
// LEAVING IS A DISSOLVE, NOT A CUT. Every way out of here into a room fades the
// page down first and the room page fades itself up on the far side, over the
// same `FADE_MS` the phase crossfade inside the room runs at. It is two halves
// rather than one overlap because nothing above the router can render both
// pages at once — through the page's own background, which is the same colour
// on both sides of the navigation, so it reads as one movement anyway. Reduced
// motion turns both halves into the cut they replaced, from the stylesheet,
// with no branch here.
// ---------------------------------------------------------------------------

import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  DISPLAY_NAME_MAX,
  ROOM_CODE_LENGTH,
  isValidRoomCode,
  normalizeRoomCode,
  validateDisplayName,
} from '@countdown/shared';
import { createRoom, getConfig, listPublicRooms, lookupRoom, type SiteConfig } from '../lib/api.js';
import { acquireTurnstileToken } from '../hooks/useSiteConfig.js';
import { useAccount } from '../hooks/useAccount.js';
import { useRoomConnection } from '../hooks/roomConnection.js';
import { usePlayerStore } from '../store/playerStore.js';
import { useGameStore, type DeadEnd } from '../store/gameStore.js';
import { AppHeader } from '../components/layout/AppHeader.js';
import { FADE_MS } from '../components/layout/PhaseCrossfade.js';
import { Button, Card, CodeInput, LetterTile } from '../components/primitives/index.js';
import type { RoomNavState } from './room/RoomPage.js';

export interface HomeScreenProps {
  /**
   * Turnstile seam. The widget is not rendered here and no script tag is added:
   * when `turnstileEnabled` is false the server accepts an empty token, and when
   * it is true the token comes from this provider. `getTurnstileToken` in
   * lib/turnstile.ts already has exactly this shape, so wiring the challenge is
   * passing it in from the app shell.
   */
  requestTurnstileToken?: (siteKey: string) => Promise<string>;
  /**
   * Sign-in seam. Clerk's provider and its card belong to the app shell, and the
   * header's account control reads the account context for both the state and
   * the action, so nothing has to be passed here. The prop stays as an override:
   * a caller that wants its own sign-in action can supply one.
   */
  onSignIn?: () => void;
}

/** The API client turns every server error into a sentence a player can act on. */
function reasonFrom(error: unknown): string {
  return error instanceof Error && error.message ? error.message : 'Could not reach the server.';
}

/** The difficulty a solo room's opening bot is seated at. Changed in the lobby. */
const SOLO_BOT_DIFFICULTY = 'medium';

/**
 * How long the join waits for Clerk before going ahead as a guest. The same
 * number and the same reason as `RoomPage`'s: playing must not become dependent
 * on a third-party script that may never arrive, and a guest seat is upgraded in
 * place when the session does land.
 */
const CLERK_WAIT_MS = 4_000;

/**
 * How long the socket half of a join is given before it is called off.
 *
 * The gates each fail with a sentence of their own; this is the one that has
 * none — a socket that connects and is never answered, or never connects at all.
 * Without it the Join button spins for as long as the player is willing to look
 * at it, which is what the old room page did too.
 */
const SEAT_TIMEOUT_MS = 15_000;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Wait for the socket to either seat the player or say why it will not.
 *
 * Subscribed rather than rendered. The alternative is an effect watching the
 * store and calling setState from inside it, which is a cascade of renders for
 * something that is not a render at all: it is the second half of one action
 * the player started, and it belongs in the same function as the first half.
 *
 * Four outcomes, and every one of them is a sentence the home screen can show:
 *
 *   - the room arrives, which is the only one that leaves this screen
 *   - the server refuses the join — full, banned, a name it will not take
 *   - the room ejects it, which is rare and still has to say which way
 *   - nothing at all happens for long enough that waiting is not honest
 */
type SeatOutcome = { ok: true } | { ok: false; reason: string };

function deadEndReason(kind: DeadEnd['kind'], reason: string | null): string {
  if (reason !== null) return reason;
  return kind === 'banned'
    ? 'You are banned from this room.'
    : kind === 'kicked'
      ? 'You were removed from this room.'
      : kind === 'displaced'
        ? 'You joined this room somewhere else.'
        : 'That room closed.';
}

function waitForSeat(): Promise<SeatOutcome> {
  return new Promise<SeatOutcome>((resolve) => {
    let unsubscribe = () => {};
    const timer = setTimeout(() => {
      unsubscribe();
      resolve({ ok: false, reason: 'Could not reach that room. Try again.' });
    }, SEAT_TIMEOUT_MS);

    const settle = (outcome: SeatOutcome): boolean => {
      clearTimeout(timer);
      unsubscribe();
      resolve(outcome);
      return true;
    };

    const read = (store: ReturnType<typeof useGameStore.getState>): boolean => {
      if (store.state !== null) return settle({ ok: true });
      if (store.joinError !== null) return settle({ ok: false, reason: store.joinError.message });
      if (store.deadEnd !== null) {
        return settle({
          ok: false,
          reason: deadEndReason(store.deadEnd.kind, store.deadEnd.reason),
        });
      }
      return false;
    };

    unsubscribe = useGameStore.subscribe(read);
    // The answer can already be in the store by the time this is called.
    read(useGameStore.getState());
  });
}

export function HomeScreen({ requestTurnstileToken, onSignIn }: HomeScreenProps) {
  const navigate = useNavigate();

  const displayName = usePlayerStore((s) => s.displayName);
  const setDisplayName = usePlayerStore((s) => s.setDisplayName);
  const deviceId = usePlayerStore((s) => s.deviceId);
  const reconnectTokens = usePlayerStore((s) => s.reconnectTokens);

  const [config, setConfig] = useState<SiteConfig | null>(null);
  const [openCount, setOpenCount] = useState<number | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState<'create' | 'join' | 'solo' | null>(null);
  // Three error slots, because they belong to three different controls and each
  // has a reserved line of its own to appear in. The join slot also carries
  // whether the code itself was the problem: a missing display name is reported
  // there because that is where the player is looking, but it is not the code's
  // fault and must not paint the boxes red.
  const [createError, setCreateError] = useState<string | null>(null);
  const [soloError, setSoloError] = useState<string | null>(null);
  const [joinError, setJoinError] = useState<{ text: string; badCode: boolean } | null>(null);

  const nameField = useRef<HTMLInputElement | null>(null);

  // --- The socket half of a join ---------------------------------------------
  // The connection belongs to the app shell, so it outlives the navigation into
  // the room. This screen opens it, waits on the same store the room page reads,
  // and navigates only once there is a room in it.
  const account = useAccount();
  const { join: openRoom, leave: closeRoom } = useRoomConnection();

  /** The code whose socket join is in flight, or null. */
  const [seating, setSeating] = useState<string | null>(null);
  /** True from the moment there is a room to go to, until this page is gone. */
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    let live = true;

    // Neither call blocks anything on this screen, so a failure is silent: an
    // open count that cannot be fetched is one that is simply not shown.
    void getConfig()
      .then((value) => {
        if (!live) return;
        setConfig(value);
        // **Solve the bot challenge now, while somebody is typing their name.**
        // Measured against production on 2026-08-05, the invisible challenge
        // took 5.2 seconds, and every one of them was spent AFTER the press with
        // `POST /api/rooms` waiting behind it — a six-second Create button whose
        // request is 355ms of that. Warming it here moves the wait into the time
        // a player was going to spend on this screen anyway.
        //
        // Nothing is shown by this. If Cloudflare decides it wants a human the
        // challenge sits mounted and invisible until a press adopts it; see
        // `warmTurnstile`. Failures are swallowed for the same reason the two
        // fetches around it are — nobody asked a question yet.
        if (value.turnstileEnabled && value.turnstileSiteKey && !requestTurnstileToken) {
          const siteKey = value.turnstileSiteKey;
          void import('../lib/turnstile.js')
            .then((module) => module.warmTurnstile(siteKey))
            .catch(() => {});
        }
      })
      .catch(() => {});

    void listPublicRooms()
      .then((rooms) => {
        if (live) setOpenCount(rooms.length);
      })
      .catch(() => {});

    return () => {
      live = false;
    };
  }, [requestTurnstileToken]);

  const nameCheck = validateDisplayName(displayName);

  async function turnstileToken(): Promise<string> {
    if (!config?.turnstileEnabled || !config.turnstileSiteKey) return '';
    // The prop is an override for tests and stories. Falling back to the real
    // challenge is what makes this screen work in the app: nothing passes the
    // prop, so without this every create and join sent an empty token and the
    // server refused it — the entire home screen stopped working the moment bot
    // protection was switched on.
    if (requestTurnstileToken) return requestTurnstileToken(config.turnstileSiteKey);
    return (await acquireTurnstileToken(config)) ?? '';
  }

  /**
   * Out of here and into the room, dissolving rather than cutting.
   *
   * Nothing is cleared on the way: `busy` stays set so the button goes on
   * spinning for the length of the fade, and this whole screen is unmounted by
   * the navigation a moment later. A caller that gets here has already decided
   * there is a room; there is no failure path out of this function.
   */
  async function leaveFor(roomCode: string, roomState?: RoomNavState) {
    setLeaving(true);
    await sleep(FADE_MS);
    // `dissolved` on every route out of here, so the room page knows a page has
    // already faded into it and does not fade in again on top. Two dissolves
    // through the same background is the stutter; see `RoomNavState.dissolved`.
    navigate(`/room/${roomCode}`, { state: { ...(roomState ?? {}), dissolved: true } });
  }

  /**
   * Create a room and open it. `solo` only decides which slot a refusal lands
   * in and whether the room page seats an opening bot: both buttons make the
   * same private room, because there is nothing about playing alone that the
   * lobby cannot change afterwards.
   *
   * The socket half is here rather than on the room page for the reason at the
   * top of this file, and it is the same code path a join by code takes. Two
   * differences, both small: there is no lookup to make of a room this device
   * has just been handed, and the socket needs no Turnstile token — the create
   * request cleared the challenge and the server holds a single-use grant
   * against this device for exactly this join.
   */
  async function create(solo: boolean) {
    if (busy !== null) return;
    const fail = solo ? setSoloError : setCreateError;

    if (!nameCheck.ok) {
      setCreateError(null);
      setSoloError(null);
      setJoinError(null);
      fail(nameCheck.error);
      // The message names the field; the cursor finds it. Between the two there
      // is nothing left to work out.
      nameField.current?.focus();
      return;
    }

    setCreateError(null);
    setSoloError(null);
    setJoinError(null);
    setBusy(solo ? 'solo' : 'create');
    try {
      // No visibility. Omitting it IS private: that is what `DEFAULT_SETTINGS`
      // says and what the route does with anything that is not the word
      // `public`. This used to send `public` and override both, and then sent
      // `private` to say the same thing the default already said — which is one
      // more place for the default to drift from itself. Listing a room
      // publicly is a switch in the lobby, where the host can see who is in it
      // first.
      const { roomCode } = await createRoom({
        displayName: nameCheck.value,
        deviceId,
        turnstileToken: await turnstileToken(),
      });

      // The same wait the join makes, for the same reason: a seat taken before
      // Clerk answers is a guest seat, and the room page used to make this wait
      // on this path. Only a moment — a session that lands later re-sends the
      // join by itself and the server upgrades the seat in place.
      if (!account.ready) await sleep(CLERK_WAIT_MS);

      // Whatever the last room left behind is not this room. Cleared before the
      // socket opens so a stale `state` cannot read as this join having already
      // succeeded.
      useGameStore.getState().clear();
      openRoom({ roomCode });

      const outcome = await waitForSeat();
      if (!outcome.ok) {
        closeRoom();
        useGameStore.getState().clear();
        setBusy(null);
        fail(outcome.reason);
        return;
      }

      await leaveFor(roomCode, {
        // `justCreated` makes RoomPage skip the lookup and the bot-protection
        // challenge. Without it the person who just created a room is sent
        // through a SECOND challenge to enter it, moments after passing one —
        // and that second challenge times out, so creating a room left you
        // unable to get into it.
        justCreated: true,
        // Solo is one seat and one bot, and then the lobby. Adding the bot is a
        // host action over the socket, and this screen's socket is the shell's
        // rather than the room's, so the room page does it on arrival. It is
        // deliberately NOT waited for here: an opponent appearing in a lobby a
        // beat after the player does is the room filling up, which is what a
        // lobby looks like. Holding the whole transition for it would be a
        // second wait with nothing on the screen.
        ...(solo ? { soloBot: SOLO_BOT_DIFFICULTY } : {}),
      });
    } catch (caught) {
      setBusy(null);
      fail(reasonFrom(caught));
    }
  }

  // `entered` comes from CodeInput's completion callback, which fires in the same
  // tick as the change that filled the last box: reading `code` there would read
  // the value from before that keystroke.
  async function handleJoin(entered?: string) {
    // The completion callback fires on every keystroke that fills the last box,
    // including one typed into a field that is already being checked.
    if (busy !== null) return;

    if (!nameCheck.ok) {
      setCreateError(null);
      setSoloError(null);
      setJoinError({ text: nameCheck.error, badCode: false });
      nameField.current?.focus();
      return;
    }

    const wanted = normalizeRoomCode(entered ?? code);
    if (!isValidRoomCode(wanted)) {
      // The same sentence the server sends for the same mistake.
      setCreateError(null);
      setSoloError(null);
      setJoinError({ text: 'That is not a valid room code.', badCode: true });
      return;
    }

    setCreateError(null);
    setSoloError(null);
    setJoinError(null);

    // A code this device already holds a reconnect token for is a room it is
    // getting back into, and a reconnect skips every gate — RoomPage skips the
    // lookup for exactly the same reason. The gate this used to be written
    // about was the room lock, which is gone; the Turnstile challenge below is
    // the one that is left, and a player coming back to a seat they already
    // hold should not be asked to prove they are a person a second time.
    if (reconnectTokens[wanted] !== undefined) {
      // Straight out, with no socket opened here. This is the one way into a
      // room that does land on the seat notice — there is a genuine connect to
      // wait for and no button on this screen still holding it — so the notice
      // holds itself up long enough to be read. See `useMinimumOnScreen`.
      void leaveFor(wanted);
      return;
    }

    setBusy('join');
    try {
      const room = await lookupRoom(wanted);

      // The challenge, here rather than on the room page. It puts up its own
      // centred card if Cloudflare decides to ask and nothing at all if it does
      // not, so this screen needs no surface for it.
      const token = await turnstileToken();

      // The same wait the room page used to make, for the same reason: a seat
      // taken before Clerk answers is a guest seat, and it is worth a moment to
      // avoid one. Only a moment — a session that lands later re-sends the join
      // by itself and the server upgrades the seat in place.
      if (!account.ready) await sleep(CLERK_WAIT_MS);

      // Whatever the last room left behind is not this room. Cleared before the
      // socket opens so a stale `state` cannot read as this join having already
      // succeeded.
      useGameStore.getState().clear();
      setSeating(room.roomCode);
      openRoom({ roomCode: room.roomCode, turnstileToken: token });

      // The socket now decides, and the wait stays on this screen: the boxes
      // stay held, the Join button stays spinning, and the line under them says
      // which of the two waits it is.
      const outcome = await waitForSeat();

      if (!outcome.ok) {
        // Cleared here rather than the moment the wait ends: on the way OUT the
        // spinner and the line under it have to stay exactly as they are for
        // the length of the fade, or the last thing the player sees is the
        // sentence stepping backwards to the wait before this one.
        setSeating(null);
        closeRoom();
        useGameStore.getState().clear();
        setBusy(null);
        setJoinError({ text: outcome.reason, badCode: true });
        return;
      }

      // Only now, with a room to navigate into. The connection is the shell's
      // and survives the navigation, so the room page's first frame is the
      // lobby rather than a second announcement of this wait.
      await leaveFor(room.roomCode);
    } catch (caught) {
      setSeating(null);
      setJoinError({ text: reasonFrom(caught), badCode: true });
      setBusy(null);
    }
  }

  const joining = busy === 'join';

  return (
    <div
      className={`flex min-h-full flex-col transition-opacity duration-150 ease-out ${
        leaving ? 'opacity-0' : 'opacity-100'
      }`}
    >
      <AppHeader onSignIn={onSignIn} />

      <main className="mx-auto grid w-full max-w-[1180px] flex-1 gap-8 px-4 py-8 sm:px-8 lg:grid-cols-[minmax(0,1fr)_440px] lg:items-start lg:gap-14 lg:py-14">
        <div className="flex flex-col gap-5">
          <p className="label-mono">countdown.iyk.gg</p>
          <h1 className="text-[34px] leading-[0.98] font-extrabold tracking-[-0.03em] lg:text-[62px]">
            Nine tiles, thirty seconds.
          </h1>
          <p className="max-w-[46ch] text-[17px] leading-relaxed text-ink-muted">
            Letters, numbers and a conundrum, played against other people or a table of bots.
          </p>

          {/* The wordmark, in the game's own set piece. Sized rather than
              transform-scaled: a transform does not change layout width, so the
              scaled version still reserved the full row and pushed the page into
              horizontal scroll. Nine `wordmark` tiles come to 624px and fit. */}
          <div className="mt-2 hidden gap-1.5 lg:flex" aria-hidden="true">
            {[...'COUNTDOWN'].map((letter, index) => (
              <LetterTile key={index} letter={letter} state="available" size="wordmark" />
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-4">
          <Card>
            <div className="flex flex-col gap-2">
              <label htmlFor="display-name" className="label-mono">
                your name
              </label>
              <input
                id="display-name"
                ref={nameField}
                value={displayName}
                onChange={(event) => setDisplayName(event.target.value)}
                maxLength={DISPLAY_NAME_MAX}
                autoComplete="nickname"
                spellCheck={false}
                placeholder="What everyone sees"
                className="min-h-12 rounded-[11px] border border-line bg-surface-2 px-3.5 text-[17px] font-medium text-ink transition-colors duration-150 outline-none placeholder:text-ink-dim hover:border-line-strong focus:border-blue-400 focus:bg-surface"
              />
            </div>
          </Card>

          <Card className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Button
                variant="primary"
                size="lg"
                block
                onClick={() => void create(false)}
                disabled={busy !== null}
                loading={busy === 'create'}
              >
                Create a room
              </Button>
              {/* One reserved line, empty until it holds the reason the room was
                  refused. The note that used to stand in it went with the public
                  default it described; the height stays, because an error that
                  appears from nowhere shoves everything below it down the page. */}
              <div className="min-h-[18px]">
                {createError !== null && (
                  <p role="alert" className="text-[13px] leading-[18px] text-alert">
                    {createError}
                  </p>
                )}
              </div>
            </div>

            <div className="flex flex-col gap-2 border-t border-line pt-4">
              <span className="label-mono">join by code</span>
              <div className="flex flex-wrap items-center gap-3">
                {/* Pulled back by the group's own padding, so the boxes line up
                    with the label above them and the focused panel has room to
                    sit around them without touching anything. */}
                <div className="-ml-2">
                  <CodeInput
                    value={code}
                    onChange={(value) => {
                      setCode(value);
                      // A code being retyped is not a code that just failed.
                      if (joinError !== null) setJoinError(null);
                    }}
                    onComplete={(value) => void handleJoin(value)}
                    disabled={joining}
                    invalid={joinError?.badCode === true}
                    describedBy="join-code-note"
                  />
                </div>
                <Button
                  variant="outline"
                  size="lg"
                  onClick={() => void handleJoin()}
                  disabled={busy !== null || normalizeRoomCode(code).length < ROOM_CODE_LENGTH}
                  loading={joining}
                >
                  Join
                </Button>
              </div>
              {/* The same reserved line. Four boxes already say the code is four
                  characters, so nothing stands here until the lookup is running
                  or has failed. */}
              <div className="min-h-[18px]">
                {joinError !== null ? (
                  <p id="join-code-note" role="alert" className="text-[13px] leading-[18px] text-alert">
                    {joinError.text}
                  </p>
                ) : (
                  <p id="join-code-note" className="text-[13px] leading-[18px] text-ink-dim">
                    {/* Two sentences for two waits, because they are two
                        different things going wrong when they are slow: the
                        first is whether the room is there at all, the second is
                        whether there is room in it. The second one is the line
                        the old "Getting you a seat" page carried, kept because
                        it was the right sentence — it just did not need a page
                        of its own to say it on. */}
                    {/* Only ever while THIS control is the one working. A
                        create opens a socket too, and the wait under the code
                        boxes is not the wait a create is having. */}
                    {!joining ? '' : seating !== null ? 'Getting you a seat.' : 'Looking for that room.'}
                  </p>
                )}
              </div>
            </div>

            <div className="flex flex-col gap-2 border-t border-line pt-4">
              <div className="grid gap-3 sm:grid-cols-2">
                <Button variant="outline" size="lg" block onClick={() => navigate('/browse')}>
                  Public games
                  <OpenCount value={openCount} />
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  block
                  onClick={() => void create(true)}
                  disabled={busy !== null}
                  loading={busy === 'solo'}
                >
                  Play solo
                </Button>
              </div>
              {/* Solo creates a room too, so it can be refused for the same
                  reasons, and it gets a line of its own next to the button that
                  was pressed rather than one at the top of the card. */}
              <div className="min-h-[18px]">
                {soloError !== null && (
                  <p role="alert" className="text-[13px] leading-[18px] text-alert">
                    {soloError}
                  </p>
                )}
              </div>
            </div>
          </Card>

          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            <Link to="/how-to-play" className="w-fit text-[16px] font-semibold text-blue-200">
              How to play
            </Link>
            {/* The AI opponents have a record of their own, and this is the only
                way into it that does not require having already played one. */}
            <Link to="/models" className="w-fit text-[16px] font-semibold text-blue-200">
              AI opponents
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}

/**
 * How many rooms are open, as a badge on the button rather than a second line
 * under it. The slot is the same size before and after the count arrives, so the
 * button does not change shape a moment after the page paints.
 */
function OpenCount({ value }: { value: number | null }) {
  return (
    <span
      aria-hidden={value === null || undefined}
      className={`grid h-6 min-w-6 place-items-center rounded-full bg-blue-700/40 px-1.5 font-mono text-[12px] font-semibold text-blue-200 tabular transition-opacity duration-200 ${
        value === null ? 'opacity-0' : 'opacity-100'
      }`}
    >
      <span aria-hidden="true">{value ?? 0}</span>
      {/* The digit alone is clear beside the label it sits on, and unreadable
          on its own in a screen reader's list of buttons. */}
      <span className="sr-only">{value ?? 0} open</span>
    </span>
  );
}
